import styled from "styled-components";
import { t } from "i18next";
import { Schedule } from "../types/Tournament";
import { Text } from "./styles";
import { ROUND_ACTIVE_DAYS } from "../config";

type Props = {
  selectedRoundIndex: number;
  bracketSize: number;
  groupSize: number;
  schedule: Schedule;
  startDateLabel: string;
  locked: boolean;
  onPrev: () => void;
  onNext: () => void;
};

const roundLabel = (roundIndex: number, totalRounds: number): string => {
  if (roundIndex === totalRounds - 1) return t("FINAL_ROUND");
  return `${t("ROUND")} ${roundIndex + 2}`;
};

const phaseSubtitle = (
  schedule: Schedule,
  selectedRoundIndex: number,
  startDateLabel: string,
): string => {
  if (schedule.phase === "pre") {
    return t("STARTS_ON", { date: startDateLabel });
  }
  if (schedule.phase === "done") {
    return t("FINISHED");
  }
  if (selectedRoundIndex !== schedule.currentRoundIndex) {
    return "";
  }
  if (schedule.phase === "active") {
    return `${t("DAY")} ${schedule.dayInRound}/${ROUND_ACTIVE_DAYS}`;
  }
  return t("PAUSE_WINNERS"); // pause
};

const RoundNav = ({
  selectedRoundIndex,
  bracketSize,
  groupSize,
  schedule,
  startDateLabel,
  locked,
  onPrev,
  onNext,
}: Props) => {
  const teamsInRound = bracketSize / Math.pow(groupSize, selectedRoundIndex);
  const subtitle = locked
    ? t("LOCKED")
    : phaseSubtitle(schedule, selectedRoundIndex, startDateLabel);
  const canPrev = selectedRoundIndex > 0;
  const canNext = selectedRoundIndex < schedule.totalRounds - 1;

  return (
    <Nav>
      <ArrowButton disabled={!canPrev} onClick={canPrev ? onPrev : undefined}>
        <Text
          fontSize={20}
          fontColor={canPrev ? "#000000" : "#cccccc"}
          fontWeight={"bold"}
        >
          ‹
        </Text>
      </ArrowButton>
      <Center>
        <Text fontSize={16} fontColor={"#000000"} fontWeight={"bold"}>
          {locked ? "🔒 " : ""}
          {roundLabel(selectedRoundIndex, schedule.totalRounds)} ·{" "}
          {teamsInRound} {t("TEAMS")}
        </Text>
        {subtitle ? (
          <Text fontSize={12} fontColor={"#555555"}>
            {subtitle}
          </Text>
        ) : null}
      </Center>
      <ArrowButton disabled={!canNext} onClick={canNext ? onNext : undefined}>
        <Text
          fontSize={20}
          fontColor={canNext ? "#000000" : "#cccccc"}
          fontWeight={"bold"}
        >
          ›
        </Text>
      </ArrowButton>
    </Nav>
  );
};

const Nav = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ArrowButton = styled.div<{ disabled?: boolean }>`
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  padding: 0 12px;
  user-select: none;
`;

export default RoundNav;
